/**
 * Planting dates for the plants still to plant: when to start each one
 * indoors, plant it out and sow it outside, for the garden's own area.
 *
 * From GET /api/plants/planting-windows (the backend's plantingDatesService),
 * worked out by Grow's temperature-curve method from the garden's weather
 * station and each crop's biology. The backend sends the garden's Planning
 * and Seeding plants, and its "Not sure" ones (nothing recorded; their card
 * asks whether they are planted yet), and each crop's windows once:
 *
 * - source 'for_your_area': the crop's own timing ("For your area").
 * - source 'rough': its kind's general timing, or for a flower a rule by how
 *   hardy it is; said as a rough date, so it never passes for Grow's own.
 * - source null: no dates, and the app shows nothing for it.
 *
 * Each crop carries its group's names in English (words: "pepper",
 * "peppers"), for lines about it in English ("Last week to start peppers
 * indoors"); the app speaks of a plant by its own name in Spanish, as there
 * are no Spanish names for them yet.
 *
 * Dates are "MM-DD" with no year; the backend places them around the
 * gardener's day and says them (the plants brief's planting dates,
 * services/plants/plantingSaid; D40).
 *
 * getCropPlantingDates asks for crops not yet in the garden, by their plant
 * ids, on the garden's own day (the server's, from its time zone; no day is
 * sent, architecture audit A31): the seed-packet scan's review sheet ("first
 * to start: Tomato, Feb 18"), which crop is first being the backend's answer.
 *
 * Endpoint: GET /plants/planting-windows?zip=&crops=1,2,3&groups=7 →
 * { success, plants, crops, lastFrost, first, groupDefaults }. groups are crops whose Type is "Not sure" (B52): the server
 * adds each crop's default type to the crops read and says which in groupDefaults ({ cropId: plantId }); the app only
 * matches its rows by it.
 *
 * Who uses it: features/packets PacketReview. (The Plants page's cards read
 * the plants brief, GET /plants/brief.) Caching:
 * [PLANTING_CROP_DATES_QUERY_KEY, zip, crops, the garden's day] (the garden
 * place's postcode and day), PLANTING_DATES_STALE_MS.
 * Auth: the signed-in token, in the garden being tended.
 * In a shared garden no ?zip is sent (gardenMembers.gardenZip), so the
 * backend reads the garden at its owner's postcode.
 *
 * @module api/plantingDates
 */

import { apiClient } from './apiClient';
import { gardenZip } from './gardenMembers';

/** How a crop is put in the ground: started indoors, planted out, or sown (or planted) outside. */
export type PlantingMethod = 'start_indoors' | 'transplant' | 'direct_seed';

/** The season a window belongs to; winter is an overwintering crop's, or a window over New Year. */
export type PlantingSeason = 'spring' | 'fall' | 'winter';

/** A span of days, "MM-DD" each; the close is earlier than the open when it runs over New Year. */
export interface DaySpan {
  open: string;
  close: string;
}

/** One window: a method in a season, every day it can work (open–close) and Grow's best days inside it. */
export interface PlantingWindow extends DaySpan {
  season: PlantingSeason;
  method: PlantingMethod;
  /** The best days, or null when every day is as good (a rough date has none). */
  best: DaySpan | null;
  /** Why it opens and closes when it does, in both languages. */
  reason: { text: string; text_es: string };
}

/** One crop's dates at the garden's station. */
export interface CropPlantingDates {
  source: 'for_your_area' | 'rough' | null;
  /** What goes in the ground instead of seed (garlic cloves, onion sets), or null for seed. */
  plantedAs: 'cloves' | 'sets' | null;
  /** How long it grows indoors before planting out, in days, when that is known. */
  indoorDays: number | null;
  /** A flower's rough date by hardiness: after the last frost (tender) or some weeks before (hardy). */
  flower: { hardiness: 'tender' | 'hardy'; date: string; lastFrost: string } | null;
  /** Its group's names in English, lower case ("pepper", "peppers"); either may be null. */
  words: { one: string | null; many: string | null };
  windows: PlantingWindow[];
}

/** The garden's dates: its plants still to plant, and each crop's windows. */
export interface GardenPlantingDates {
  /** The plants, with the crop each one is; stage as the backend read it ('not_sure': nothing recorded). */
  plants: Array<{ plantObjId: number; plantId: number; stage: 'planning' | 'seeding' | 'not_sure' | null }>;
  /** Each crop's dates, by its plant id. */
  crops: Record<string, CropPlantingDates>;
  /** The typical (p50) last spring frost, "MM-DD", or null. */
  lastFrost: string | null;
}

/** Which of some crops is first to start, and when (the backend's services/planting/firstToStart). */
export interface FirstToStart {
  /** The crop, by plant id. */
  plantId: number;
  /** The first day it can be started, "YYYY-MM-DD": today when its window is open. */
  start: string;
  /** Whether its window is open today. */
  isOpen: boolean;
  /** C118 F7: the crop's finished name in the reader's language ("Tomato", "Tomate"); null when the server has no name for it. */
  name: string | null;
  /** W23 step 10: the finished sentence, "First to start: Tomato, Feb 18" or "..., now", in the reader's language; null when the crop has no name. The app draws it as sent. */
  text: string | null;
}

/** What getCropPlantingDates answers. */
export interface CropPlantingAnswer {
  /** Each crop's dates by its plant id; none for a postal code without a station. */
  crops: Record<string, CropPlantingDates>;
  /** Which of them is first to start on the day asked, or null when none has dates (and null when the garden has no postal code: the server then sends none). */
  first: FirstToStart | null;
  /** For each crop asked by `groups` (a row on Type "Not sure"), the plant id the server read its dates under (B52). */
  groupDefaults: Record<string, number>;
}

/**
 * The planting dates for some crops at the garden's postal code, whether or
 * not the garden grows them yet, and which of them is first to start on the
 * garden's own day (the seed-packet review's line; the backend decides it).
 *
 * @param zip - The garden's postcode (useGardenPostcode); sent only in the person's own garden (gardenZip).
 * @param plantIds - The crops, by plant id.
 * @param cropIds - Crops (plant groups) whose Type is "Not sure": the server reads their default types (B52).
 * @returns The crops' dates, the first to start, and which plant each `cropIds` crop was read as.
 */
export async function getCropPlantingDates(zip: string, plantIds: number[], cropIds: number[] = []): Promise<CropPlantingAnswer> {
  const response = await apiClient.get<{ success: boolean; first?: FirstToStart | null; groupDefaults?: Record<string, number> } & GardenPlantingDates>('/plants/planting-windows', {
    params: { zip: gardenZip(zip), crops: plantIds.join(','), ...(cropIds.length ? { groups: cropIds.join(',') } : {}) },
  });
  return { crops: response.data.crops ?? {}, first: response.data.first ?? null, groupDefaults: response.data.groupDefaults ?? {} };
}
